import Button from '../button'
import { motion } from 'framer-motion'
import { useEffect, useMemo, useRef } from 'react'
import useCarouselState from '@/app/(site)/(static)/about/sections/values/hooks/use-carousel-state'
import { cn } from '@/util/cn'

type Props = {
    state: ReturnType<typeof useCarouselState>
    className?: string
}

const smoothCenterScroll = (container: HTMLDivElement, item: HTMLElement) => {
    const containerRect = container.getBoundingClientRect()
    const itemRect = item.getBoundingClientRect()
    const itemCenter = item.offsetLeft - container.offsetLeft + itemRect.width / 2

    /**
     * Target so that itemCenter lands at the middle of the container
     */
    const target = itemCenter - containerRect.width / 2
    const max = container.scrollWidth - container.clientWidth
    const clamped = Math.max(0, Math.min(target, max))
    container.scrollTo({ left: clamped, behavior: 'smooth' })
}

const MobileNavigationList = ({ state, className }: Props) => {
    const containerRef = useRef<HTMLDivElement>(null)

    const itemRefs = useMemo(
        () => state.slides.map(() => ({ current: null as HTMLButtonElement | null })),
        [state.slides]
    )

    useEffect(() => {
        /**
         * Scroll only when the active index changes and we are not mid-animation
         */
        if (state.isAnimating) {
            return
        }

        const container = containerRef.current
        const item = itemRefs[state.activeIndex]?.current

        if (!container || !item) {
            return
        }

        const id = requestAnimationFrame(() => smoothCenterScroll(container, item))

        return () => cancelAnimationFrame(id)
    }, [state.activeIndex, state.isAnimating, itemRefs])

    const handleButtonRef = (element: HTMLButtonElement | null, index: number) => {
        if (!element) {
            return
        }

        itemRefs[index].current = element
    }

    return (
        <motion.div
            layout
            ref={containerRef}
            className={cn(
                'flex max-w-full flex-nowrap gap-3 overflow-x-auto rounded-full bg-giphyDarkestGrey',
                'no-scrollbar scroll-pb-2 scroll-pl-3 scroll-smooth',
                className
            )}
        >
            {state.slides.map((slide, index) => (
                <Button
                    gradientClassName={slide.gradientClassName}
                    isActive={state.isActiveSlide(index)}
                    onClick={() => state.setActiveIndex(index)}
                    key={index}
                    ref={(ref) => handleButtonRef(ref, index)}
                    classNames={{ base: 'scroll-ml-3 scroll-mr-3 snap-start', text: 'whitespace-nowrap px-4' }}
                >
                    {slide.name}
                </Button>
            ))}
        </motion.div>
    )
}

export default MobileNavigationList
